import React from "react";
import { cn } from "@/lib/utils";
import { Users, TrendingDown, AlertTriangle } from "lucide-react";

interface GenerationalRatioChartProps {
  title?: string;
  data?: { year: number; ratio: number }[];
  animated?: boolean;
  className?: string;
}

const DEFAULT_DATA = [
  { year: 1997, ratio: 4.5 },
  { year: 2017, ratio: 3.2 },
  { year: 2037, ratio: 2.0 },
];

export function GenerationalRatioChart({
  title = "Erwerbsfähige pro Pensionist",
  data = DEFAULT_DATA,
  animated = true,
  className,
}: GenerationalRatioChartProps) {
  const maxRatio = Math.max(...data.map((d) => d.ratio));

  const getColorClass = (ratio: number) => {
    if (ratio >= 4) return "bg-emerald-500";
    if (ratio >= 3) return "bg-amber-500";
    return "bg-rose-500";
  };

  const getTextColorClass = (ratio: number) => {
    if (ratio >= 4) return "text-emerald-600";
    if (ratio >= 3) return "text-amber-600";
    return "text-rose-600";
  };

  return (
    <div className={cn("space-y-6", className)}>
      {title && (
        <div className="text-center space-y-2">
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm text-muted-foreground">
            Demografischer Wandel: Weniger Beitragszahler pro Pensionist
          </p>
        </div>
      )}

      <div className="flex items-end justify-center gap-8 h-64">
        {data.map((item, idx) => {
          const heightPercent = (item.ratio / maxRatio) * 100;

          return (
            <div
              key={item.year}
              className={cn(
                "flex flex-col items-center",
                animated && "animate-in fade-in slide-in-from-bottom-4"
              )}
              style={animated ? { animationDelay: `${idx * 150}ms` } : undefined}
            >
              {/* People icons */}
              <div
                className="flex flex-col items-center justify-end transition-all duration-700"
                style={{ height: `${heightPercent}%`, minHeight: "60px" }}
              >
                <div className="flex flex-wrap justify-center gap-1 mb-2">
                  {Array.from({ length: Math.ceil(item.ratio) }).map((_, i) => (
                    <Users
                      key={i}
                      className={cn(
                        "h-5 w-5",
                        i < Math.floor(item.ratio)
                          ? getTextColorClass(item.ratio)
                          : "text-muted-foreground/30"
                      )}
                    />
                  ))}
                </div>

                {/* Bar */}
                <div
                  className={cn(
                    "w-16 rounded-t-lg transition-all duration-700",
                    getColorClass(item.ratio)
                  )}
                  style={{ height: `${heightPercent * 0.6}%`, minHeight: "40px" }}
                />
              </div>

              {/* Ratio value */}
              <div
                className={cn(
                  "mt-2 text-2xl font-bold",
                  getTextColorClass(item.ratio)
                )}
              >
                {item.ratio.toFixed(1)}
              </div>

              {/* Year */}
              <div className="text-sm font-medium mt-1">{item.year}</div>
            </div>
          );
        })}
      </div>

      {/* Warning message */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Die gesetzliche Pension allein wird nicht reichen
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Immer weniger Erwerbstätige finanzieren immer mehr Pensionisten.
            Private Vorsorge wird unverzichtbar.
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span>Stabil (≥4)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500" />
          <span>Angespannt (3-4)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-rose-500" />
          <span>Kritisch (&lt;3)</span>
        </div>
      </div>
    </div>
  );
}
